import { Product } from './Product';

export type OrderStatus =
  | 'pending'
  | 'paid'
  | 'fulfilled'
  | 'cancelled'
  | 'refund'
  | 'open-draft'
  | 'completed-draft'
  | 'invoice_sent-draft';

export const OrderStatuses = [
  'pending',
  'paid',
  'fulfilled',
  'cancelled',
  'refund',
  'open-draft',
  'completed-draft',
  'invoice_sent-draft',
] as OrderStatus[];

export type OrderInfo = {
  status: OrderStatus[];
};

export interface GenericOrder {
  isDummy?: boolean;
  status: OrderStatus;
  createdAt: Date;
  updatedAt: Date;
  name: string;
  email: string;
  total_price: string;
  currency: string;
  addressess: Addressess;
  customer: Customer;
  metafields: Record<string, string | undefined>;
}
export interface DraftOrder extends GenericOrder {
  id?: number;
  type: 'draft';
  line_items: DraftOrderLineItem[];
  source: {
    id: number;
    type: 'shopify_draft_order';
  };
}

export function isDraftOrder(order: DraftOrder | Order): order is DraftOrder {
  if ((order as DraftOrder).type === 'draft') {
    return true;
  }
  return false;
}

export function getTriggerId(order: DraftOrder | Order | Product): string {
  if (order.type === 'product') {
    return order.id;
  }
  if (isDraftOrder(order)) {
    return order.source.id.toString();
  }

  return order.id.toString();
}

export interface Order extends GenericOrder {
  type: 'order';
  id: number;
  closedAt?: Date;
  line_items: Lineitem[];
}

export interface Customer {
  id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: any;
  default_address?: Defaultaddress;
  metafields: Record<string, string | undefined>;
}

export interface Defaultaddress {
  id?: number;
  customer_id?: number;
  first_name?: string;
  last_name?: string;
  company?: string | null;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  country?: string;
  zip?: string;
  phone?: string | null;
  name?: string;
  province_code?: any;
  country_code?: string;
  country_name?: string;
  default?: boolean;
}

interface ILineItemDiscountAllocation {
  amount: string;
  amount_set: IMoneySet;
}

interface DraftOrderDiscount {
  amount: string;
  description: string;
  non_applicable_reason: string;
  title: string;
  value: string;
  value_type: 'fixed_amount' | 'percentage';
}
export interface DraftOrderLineItem {
  applied_discount: DraftOrderDiscount | null;
  discount_codes: any[];
  fulfillment_service: string;
  gift_card: boolean;
  grams: number;
  key: string;
  line_price: string;
  name: string;
  origin_location_id: number;
  price: string;
  product_id: number;
  properties: any | null;
  quantity: number;
  requires_shipping: boolean;
  sku: string;
  taxable: boolean;
  tax_lines: ICheckoutTaxLine[];
  title: string;
  variant_id: number;
  variant_title: string;
  vendor: string;
}
export interface Lineitem {
  // Core identifiers
  product_id: number | null;
  variant_id: number | null;
  inventory_id: number | null;

  // Price and quantity
  price: string;
  quantity: number;

  // Product details
  title: string;
  name: string;
  vendor: string;

  // Tax information
  taxRate?: number;
  taxPrice?: string;
  description?: string;

  // Additional fields specific to Lineitem
  id: number;
  tax_lines: ILineItemTaxLine[];
  discount_allocations: ILineItemDiscountAllocation[];
  pre_tax_price?: string;
  pre_tax_price_set?: IMoneySet;
  price_set: IMoneySet;
  properties: IOrderLineItemProperty[];
  variant_title: string | null;
  taxable: boolean;
}

interface ILineItemTaxLine {
  title: string;
  price: string;
  rate: number;
  price_set: IMoneySet;
}

interface ICheckoutTaxLine {
  compare_at: number;
  position: number;
  price: string;
  rate: number;
  source: string;
  title: string;
  zone: string;
}

interface IOrderLineItemProperty {
  name: string;
  value: string;
}

export interface LineItemTaxLine {
  title: string;
  price: string;
  rate: number;
  price_set: IMoneySet;
}

interface IMoneySet {
  shop_money: IMoney;
  presentment_money: IMoney;
}

interface IMoney {
  amount: number | string;
  currency_code: string;
}

interface Addressess {
  shipping_address?: Shippingaddress;
  billing_address?: Shippingaddress;
}

export interface Shippingaddress {
  first_name: string;
  address1: string;
  phone: string | null;
  city: string;
  zip: string;
  province?: string;
  country: string;
  last_name: string;
  address2?: string;
  company: string | null;
  latitude: string;
  longitude: string;
  name: string;
  country_code: string;
  province_code?: any;
}
